"use client";

import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useColorScheme } from "@mui/material/styles";
import { Sun as SunIcon } from "@phosphor-icons/react/dist/ssr/Sun";
import { Moon as MoonIcon } from "@phosphor-icons/react/dist/ssr/Moon";
import { Devices as DevicesIcon } from "@phosphor-icons/react/dist/ssr/Devices";

import type { Mode } from "@apal/shared-theme";

/**
 * Mode cycle order: light -> dark -> system -> light
 */
const MODE_CYCLE: Mode[] = ["light", "dark", "system"];

/**
 * Mode display names for tooltip
 */
const MODE_LABELS: Record<Mode, string> = {
  light: "Light mode",
  dark: "Dark mode",
  system: "System preference",
};

/**
 * Get icon component for current mode
 */
function getModeIcon(mode: Mode): React.JSX.Element {
  switch (mode) {
    case "light": {
      return <SunIcon data-testid="sun-icon" />;
    }
    case "dark": {
      return <MoonIcon data-testid="moon-icon" />;
    }
    case "system": {
      return <DevicesIcon data-testid="devices-icon" />;
    }
    default: {
      return <SunIcon data-testid="sun-icon" />;
    }
  }
}

/**
 * ThemeToggleButton - Cycles through light/dark/system modes
 *
 * FIX-042: Issue #2 - Add theme toggle button to header
 * Position: Between NotificationsButton and UserButton
 * Behavior: Single click cycles light -> dark -> system -> light
 */
export function ThemeToggleButton(): React.JSX.Element {
  const { mode, setMode } = useColorScheme();

  // Default to 'system' if mode is undefined/null
  const currentMode: Mode = (mode as Mode) || "system";

  const handleToggle = (): void => {
    const currentIndex = MODE_CYCLE.indexOf(currentMode);
    const nextIndex = (currentIndex + 1) % MODE_CYCLE.length;
    const nextMode = MODE_CYCLE[nextIndex];
    setMode(nextMode);
  };

  return (
    <Tooltip title={MODE_LABELS[currentMode]}>
      <IconButton
        data-testid="theme-toggle-button"
        onClick={handleToggle}
        aria-label={`Current theme: ${MODE_LABELS[currentMode]}. Click to change.`}
      >
        {getModeIcon(currentMode)}
      </IconButton>
    </Tooltip>
  );
}

export default ThemeToggleButton;
